import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Loader2, Sparkles, Eye, Award } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { ModuleFeedback } from '../components/ModuleFeedback';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Modulo95VisionOperativa() {
    const [enfoque, setEnfoque] = useState('');
    const [respuesta, setRespuesta] = useState('');
    const [manejoDificultad, setManejoDificultad] = useState('');
    const [sistema, setSistema] = useState('');
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [aiInsight, setAiInsight] = useState('');
    const [nivelClaridad, setNivelClaridad] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/draft/vision_operativa`, { withCredentials: true });
                if (data.data) {
                    if (data.data.enfoque) setEnfoque(data.data.enfoque);
                    if (data.data.respuesta) setRespuesta(data.data.respuesta);
                    if (data.data.manejoDificultad) setManejoDificultad(data.data.manejoDificultad);
                    if (data.data.sistema) setSistema(data.data.sistema);
                }
                if (data.read_only) { setReadOnly(true); setSubmittedAt(data.submitted_at); }
            } catch {} finally { setLoadingDraft(false); }
        })();
    }, []);

    const saveDraft = useCallback(async () => {
        setSavingDraft(true);
        try { await axios.post(`${API_URL}/api/user/draft/save`, { module_id: 'vision_operativa', data: { enfoque, respuesta, manejoDificultad, sistema } }, { withCredentials: true }); } catch {} finally { setSavingDraft(false); }
    }, [enfoque, respuesta, manejoDificultad, sistema]);

    useEffect(() => { if (loadingDraft || readOnly) return; const t = setTimeout(saveDraft, 2000); return () => clearTimeout(t); }, [enfoque, respuesta, manejoDificultad, sistema, saveDraft, loadingDraft, readOnly]);

    const canSubmit = !readOnly && (enfoque.trim().length > 0 && respuesta.trim().length > 0 && manejoDificultad.trim().length > 0 && sistema.trim().length > 0);

    const handleSubmit = async () => {
        if (readOnly) return;
        if (!canSubmit) return;
        setLoading(true);
        try {
            const { data } = await axios.post(`${API_URL}/api/user/modules/vision-operativa/submit`, {
                enfoque, respuesta, manejo_dificultad: manejoDificultad, sistema
            }, { withCredentials: true });
            setAiInsight(data.ai_insight);
            setNivelClaridad(data.nivel_claridad);
            setSubmitted(true);
            toast.success('¡Proceso SENDA completado!');
        } catch (error) { toast.error(error.response?.data?.detail || 'Error al enviar'); }
        finally { setLoading(false); }
    };

    if (loadingDraft) return <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" /></div>;

    if (submitted) return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <div className="flex-1 flex items-center justify-center p-4">
                <Card className="w-full max-w-xl bg-white border border-[#DCE3E8] rounded-2xl shadow-xl"><CardContent className="pt-8 text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#5FAF9D] to-[#3E5F6E] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"><Eye className="h-10 w-10 text-white" /></div>
                    <h2 className="text-2xl font-light text-[#1F2A30] font-['Outfit'] mb-2" data-testid="vision-operativa-success-title">¡Proceso SENDA completado!</h2>
                    <p className="text-[#7A9BA8] font-medium mb-4">Tu visión operativa define cómo funcionarás de aquí en adelante</p>
                    {nivelClaridad && <div className="inline-block px-4 py-1.5 bg-[#5FAF9D]/10 text-[#5FAF9D] rounded-full text-sm font-medium mb-4">Claridad: {nivelClaridad}</div>}
                    {aiInsight && <div className="bg-[#F5F7F8] border-l-4 border-[#5FAF9D] p-4 rounded-r-xl text-left mb-6"><div className="flex items-start gap-3"><Sparkles className="h-5 w-5 text-[#5FAF9D] shrink-0 mt-0.5" /><p className="text-[#1F2A30] italic text-sm">"{aiInsight}"</p></div></div>}
                    <div className="mb-6"><ModuleFeedback moduleId="vision_operativa" chapter={9} /></div>
                    <div className="flex flex-col gap-3">
                        <Link to="/usuario/certificado"><Button className="bg-gradient-to-r from-[#C4A35A] to-[#D4B96A] hover:opacity-90 text-white px-8 py-3 rounded-xl w-full" data-testid="go-to-certificate"><Award className="h-4 w-4 mr-2" />Ver mi certificado</Button></Link>
                        <Link to="/usuario"><Button variant="outline" className="border-[#3E5F6E] text-[#3E5F6E] px-8 py-3 rounded-xl w-full" data-testid="continue-after-vision-operativa">Volver a mi espacio</Button></Link>
                    </div>
                </CardContent></Card>
            </div><SENDAFooter /></div>
    );

    return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <header className="bg-white/80 backdrop-blur-xl border-b border-[#EAE8E3]/50 sticky top-0 z-50">
                <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
                    <Link to="/usuario" className="flex items-center gap-2 text-[#3E5F6E] hover:text-[#2F4A57]"><ArrowLeft className="h-5 w-5" /><span className="text-sm font-medium">Volver</span></Link>
                    <span className="text-xs text-[#9AA5B1]">{savingDraft ? 'Guardando...' : 'Borrador guardado'}</span>
                </div>
            </header>
            <main className="flex-1 max-w-3xl mx-auto px-4 py-8 w-full">
                                {readOnly && <ReadOnlyBanner submittedAt={submittedAt} />}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2"><div className="w-10 h-10 bg-gradient-to-br from-[#5FAF9D] to-[#3E5F6E] rounded-xl flex items-center justify-center"><Eye className="h-5 w-5 text-white" /></div><span className="text-xs font-semibold text-[#9AA5B1] uppercase tracking-wider">Capítulo 9 · Módulo 9.5 — Final</span></div>
                    <h1 className="text-2xl sm:text-3xl font-light text-[#1F2A30] font-['Outfit'] mb-2">Visión operativa</h1>
                    <p className="text-[#616E7C]">Define tu sistema operativo personal para el futuro. Este es el cierre de tu proceso SENDA.</p>
                </div>
                <div className="space-y-6">
                    <div>
                        <Label className="text-sm font-semibold text-[#1F2A30] mb-2 block">Enfoque</Label>
                        <p className="text-xs text-[#9AA5B1] mb-2">¿Cómo enfocas tu energía y atención ahora?</p>
                        <Textarea value={enfoque} onChange={e => setEnfoque(e.target.value)} placeholder="Mi enfoque ahora es..." className="min-h-[80px] border-[#DCE3E8] focus:border-[#3E5F6E]" data-testid="vision-enfoque" />
                    </div>
                    <div>
                        <Label className="text-sm font-semibold text-[#1F2A30] mb-2 block">Respuesta</Label>
                        <p className="text-xs text-[#9AA5B1] mb-2">¿Cómo respondes ante las tareas y compromisos?</p>
                        <Textarea value={respuesta} onChange={e => setRespuesta(e.target.value)} placeholder="Mi forma de responder es..." className="min-h-[80px] border-[#DCE3E8] focus:border-[#3E5F6E]" data-testid="vision-respuesta" />
                    </div>
                    <div>
                        <Label className="text-sm font-semibold text-[#1F2A30] mb-2 block">Manejo de la dificultad</Label>
                        <p className="text-xs text-[#9AA5B1] mb-2">¿Qué haces cuando las cosas se complican?</p>
                        <Textarea value={manejoDificultad} onChange={e => setManejoDificultad(e.target.value)} placeholder="Cuando se complica, yo..." className="min-h-[80px] border-[#DCE3E8] focus:border-[#3E5F6E]" data-testid="vision-manejo-dificultad" />
                    </div>
                    <div>
                        <Label className="text-sm font-semibold text-[#1F2A30] mb-2 block">Tu sistema</Label>
                        <p className="text-xs text-[#9AA5B1] mb-2">Resume en pocas líneas tu sistema operativo personal completo.</p>
                        <Textarea value={sistema} onChange={e => setSistema(e.target.value)} placeholder="Mi sistema personal es..." className="min-h-[100px] border-[#DCE3E8] focus:border-[#3E5F6E]" data-testid="vision-sistema" />
                    </div>
                    <Button onClick={handleSubmit} disabled={!canSubmit || loading} className="w-full bg-gradient-to-r from-[#5FAF9D] to-[#3E5F6E] hover:opacity-90 text-white py-3 rounded-xl" data-testid="submit-vision-operativa">
                        {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Finalizando proceso...</> : 'Completar visión operativa'}
                    </Button>
                </div>
            </main>
            <SENDAFooter />
        </div>
    );
}
